import React, { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState } from "react";
import { useHistory, useLocation } from "react-router-dom";
import { HiClipboardDocumentList } from "react-icons/hi2";
import ContentWrapper from "../../components/common/AssessmentContentWrapper";
import { AddButton } from "../../components/common/MainButtonComponent";
import SectionTopicHeader from "../../components/common/SectionTopicHeader";
import { Skeleton, Stack } from "@mui/material";
import { routes } from "../../helper/routes";
import AssessSectionList from "./AssessSectionList";
import Filter from "./Filter";
import AssessmentDetailPage from "./AssessmentDetailPage";
import "./AssessSections.css";
import * as assessSectionsService from "../../service/assessSections/assessSectionsService/assessSectionsService";
import { sweetSuccessAlert, sweetErrorAlert, sweetConfirmAlert } from "../../components/common/SweetAlert";
import SetupAssessmentPage from "./SetupAssessmentPage";
import AssessmentForm from "./AssessmentForm";

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

const getErrorMessage = (error, fallback = "Please try again.") =>
  error?.message ||
  error?.response?.data?.message ||
  fallback;

const formatDate = (value) => {
  if (!value) return "";
  const normalized = typeof value === "string" ? value.trim() : value;
  const hasTimezone =
    typeof normalized === "string" && /[zZ]$|[+-]\d{2}:?\d{2}$/.test(normalized);
  const parsed =
    typeof normalized === "string"
      ? new Date(
        hasTimezone
          ? normalized
          : `${normalized.replace(" ", "T").replace(/\.(\d{3})\d+$/, ".$1")}Z`
      )
      : new Date(normalized);
  if (Number.isNaN(parsed.getTime())) return "";

  const year = parsed.getFullYear();
  const month = String(parsed.getMonth() + 1).padStart(2, "0");
  const day = String(parsed.getDate()).padStart(2, "0");

  return `${year}-${month}-${day}`;
};

const extractAssessments = (response) => {
  if (Array.isArray(response?.data?.assessments)) return response.data.assessments;
  if (Array.isArray(response?.assessments)) return response.assessments;
  return [];
};

const extractTotalCount = (response) => {
  const maybeCount =
    response?.data?.total_all_count
  const parsed = Number(maybeCount);
  if (Number.isFinite(parsed) && parsed >= 0) return parsed;
  return extractAssessments(response).length;
};

const resolveAssessmentId = (assessment) =>
  assessment?.assessment_id ?? assessment?.id ?? null;

const mapAssessmentToListItem = (assessment) => ({
  ...assessment,
  id: resolveAssessmentId(assessment),
  assessment_id: resolveAssessmentId(assessment),
  ismaster:
    assessment.ismaster !== undefined
      ? Boolean(assessment.ismaster)
      : false,
  parent_assessment_id: assessment.parent_assessment_id ?? assessment.parentAssessmentId ?? null,
  parent_assessment_title: assessment.parent_assessment_title ?? assessment.parentAssessmentTitle ?? null,
  name: assessment.title || "Untitled Assessment",
  status: assessment.assessment_state || "Unknown",
  owner: [assessment.first_name, assessment.last_name].filter(Boolean).join(" "),
  createdAt: formatDate(assessment.created_at),
  createdBy: [assessment.first_name, assessment.last_name].filter(Boolean).join(" "),
  grade: assessment.grade_id ? String(assessment.grade_id) : "",
  language: assessment.language_id ? String(assessment.language_id) : "",
  subject: assessment.subject_id ? String(assessment.subject_id) : "",
  term: assessment.term_id ? String(assessment.term_id) : "",
  author: assessment.author_id ? String(assessment.author_id) : "",
  chapter: assessment.chapter_id ? String(assessment.chapter_id) : "",
  difficulty: assessment.difficulty_id ? String(assessment.difficulty_id) : "",
  examCategory: assessment.exam_category_id ? String(assessment.exam_category_id) : "",
  source: assessment.source_id ? String(assessment.source_id) : "",
  year: assessment.year_id ? String(assessment.year_id) : "",
  // time: "-",
  // questions: 0,
});



// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

export default function AssessmentLibrarySection() {
  const history = useHistory();
  const userId = String(sessionStorage.getItem("user_id") || "");
  const assessmentsCacheRef = useRef(new Map());


  const [activeTab, setActiveTab] = useState("my");
  const [openAdd, setOpenAdd] = useState(false);

  // Assessments & pagination
  const [assessments, setAssessments] = useState([]);
  const [filteredItems, setFilteredItems] = useState([]);
  const [totalCount, setTotalCount] = useState(0);

  // Filter state (API-driven)
  const [filters, setFilters] = useState({
    limit: 20,
    offset: 0,
    sort_column: "created_at",
    sort_direction: "DESC",
    search_term: "",
    selectedAuthors: [],
    selectedChapters: [],
    selectedGrades: [],
    selectedDifficulties: [],
    selectedLanguages: [],
    selectedTerms: [],
    selectedYears: [],
    selectedsubjects: [],
    selectedStatuses: [],
    selectedExamCategories: [],
    selectedSources: [],
    selectedCreators: [],
    selectedStartDate: null,
    selectedEndDate: null,
  });

  // MetaData for filter dropdowns
  const [metaData, setMetaData] = useState(null);
  const [isMetaDataLoading, setIsMetaDataLoading] = useState(false);
  const [metaDataError, setMetaDataError] = useState(null);
  const buildMetaDataOptions = (metaData) => {
    const getMetaDataCollection = (metaDataPayload, sectionName) => {
      if (!metaDataPayload) {
        return [];
      }
      if (Array.isArray(metaDataPayload?.[sectionName])) {
        return metaDataPayload[sectionName];
      }
      if (Array.isArray(metaDataPayload?.data)) {
        const section = metaDataPayload.data.find((item) => Array.isArray(item?.[sectionName]));
        return section?.[sectionName] || [];
      }
      return [];
    };

    const mapOptions = (dataArray, idKey, labelKeys = []) =>
      (dataArray || [])
        .map((item) => {
          const labelKey = labelKeys.find(
            (key) => item?.[key] !== undefined && item?.[key] !== null,
          );
          if (
            !item ||
            item[idKey] === undefined ||
            item[idKey] === null ||
            !labelKey
          ) {
            return null;
          }
          return {
            value: String(item[idKey]),
            label: String(item[labelKey]),
          };
        })
        .filter(Boolean);

    return {
      gradeOptions: mapOptions(getMetaDataCollection(metaData, "Grade"), "grade_id", ["grade"]),
      subjectOptions: mapOptions(getMetaDataCollection(metaData, "Subject"), "subject_id", ["subject"]),
      languageOptions: mapOptions(getMetaDataCollection(metaData, "Language"), "language_id", ["language"]),
      examCategoryOptions: mapOptions(getMetaDataCollection(metaData, "Exam_Category"), "exam_category_id", ["name"]),
      termOptions: mapOptions(getMetaDataCollection(metaData, "Term"), "term_id", ["term"]),
      authorOptions: mapOptions(getMetaDataCollection(metaData, "Author"), "author_id", ["author"]),
      sourceOptions: mapOptions(getMetaDataCollection(metaData, "Source"), "source_id", ["source"]),
      createdByOptions: mapOptions(getMetaDataCollection(metaData, "Created_By"), "user_id", ["first_name", "user_code"]),
      gradingMethodOptions: mapOptions(getMetaDataCollection(metaData, "Grading_Method"), "grading_method_id", ["method_name"]),
      chapterOptions: mapOptions(getMetaDataCollection(metaData, "Chapter"), "chapter_id", ["chapter"]),
      yearOptions: mapOptions(getMetaDataCollection(metaData, "Year"), "year_id", ["year"]),
      difficultyOptions: mapOptions(getMetaDataCollection(metaData, "Difficulty"), "difficulty_id", ["difficulty_type"]),
      typeOptions: mapOptions(getMetaDataCollection(metaData, "Type"), "type_id", ["qtype"]),
      attemptMethodOptions: mapOptions(getMetaDataCollection(metaData, "Attempt_Method"), "attempt_no_id", ["no_of_attempts"]),
      notationsOptions: mapOptions(getMetaDataCollection(metaData, "notations"), "notation_id", ["notation_type"]),
      termsAndConditionsOptions: getMetaDataCollection(metaData, "Terms_And_Conditions"),
      StatusOptions: mapOptions(getMetaDataCollection(metaData, 'Status'), 'status_id', ['status']),
      durationUnitOptions: mapOptions(getMetaDataCollection(metaData, "units"), "unit_id", ["unit"]),
      unitOptions: mapOptions(getMetaDataCollection(metaData, "units"),"unit_id",["unit"]),
    };
  };
  const metaDataOptions = useMemo(() => buildMetaDataOptions(metaData), [metaData]);


  // -------------------------------------------------------------------------
  // Fetch metadata once on mount
  // -------------------------------------------------------------------------
  useEffect(() => {
    const fetchMetaData = async () => {
      try {
        setIsMetaDataLoading(true);
        setMetaDataError(null);
        const data = await assessSectionsService.getMetaDataForFilter();
        setMetaData(data);
      } catch (error) {
        setMetaDataError(error);
      } finally {
        setIsMetaDataLoading(false);
      }
    };
    fetchMetaData();
  }, []);

  // -------------------------------------------------------------------------
  // Build request filters — inject creator filter when on "my" tab
  // -------------------------------------------------------------------------
  const requestFilters = useMemo(() => {
    if (activeTab !== "my") return filters;
    return { ...filters, selectedCreators: userId ? [userId] : [] };
  }, [activeTab, filters, userId]);

  const requestKey = useMemo(() => JSON.stringify(requestFilters), [requestFilters]);

  // -------------------------------------------------------------------------
  // Fetch assessments whenever request filters change
  // -------------------------------------------------------------------------
  const fetchAssessments = useCallback(
    async (forceRefresh = false) => {
      if (!forceRefresh) {
        const cached = assessmentsCacheRef.current.get(requestKey);
        if (cached) {
          setAssessments(cached.items);
          setTotalCount(cached.totalCount);
          return;
        }
      }

      try {
        const response = await assessSectionsService.getAllAssessments(requestFilters);

        const rawAssessments = extractAssessments(response);
        const titleById = new Map(
          rawAssessments
            .map((assessment) => [String(resolveAssessmentId(assessment) || ""), assessment.title || assessment.name || ""])
            .filter(([assessmentId, title]) => assessmentId && title),
        );

        const mapped = rawAssessments.map((assessment) => {
          const item = mapAssessmentToListItem(assessment);
          if (item.parent_assessment_id && !item.parent_assessment_title) {
            item.parent_assessment_title = titleById.get(String(item.parent_assessment_id)) || null;
          }
          return item;
        });
        const nextTotalCount = extractTotalCount(response);

        assessmentsCacheRef.current.set(requestKey, {
          items: mapped,
          totalCount: nextTotalCount,
        });

        setAssessments(mapped);
        setTotalCount(nextTotalCount);
      } catch (error) {
        console.error("Error fetching assessments:", error);
        setTotalCount(0);
      }
    },
    [requestFilters, requestKey]
  );

  useEffect(() => {
    fetchAssessments();
  }, [fetchAssessments, openAdd]);

  // Keep filteredItems in sync with assessments (AssessmentLibraryFilter may override)
  useEffect(() => {
    setFilteredItems(assessments);
  }, [assessments]);

  // Reset offset when switching tabs
  useEffect(() => {
    setFilters((prev) => (prev.offset === 0 ? prev : { ...prev, offset: 0 }));
  }, [activeTab]);

  // -------------------------------------------------------------------------
  // Filter / pagination callbacks
  // -------------------------------------------------------------------------
  const handleFilterQueryChange = useCallback((nextFilters) => {
    setFilters((prev) => ({ ...prev, ...nextFilters, offset: 0 }));
  }, []);

  const currentPage = useMemo(() => {
    const safeLimit = Number(filters.limit) || 1;
    const safeOffset = Number(filters.offset) || 0;
    return Math.floor(safeOffset / safeLimit) + 1;
  }, [filters.limit, filters.offset]);

  const handlePageChange = useCallback((nextPage) => {
    setFilters((prev) => {
      const safeLimit = Number(prev.limit) || 20;
      return { ...prev, offset: Math.max(0, (nextPage - 1) * safeLimit) };
    });
  }, []);

  const handlePageSizeChange = useCallback((nextSize) => {
    setFilters((prev) => ({ ...prev, limit: nextSize, offset: 0 }));
  }, []);

  // -------------------------------------------------------------------------
  // Navigation helpers
  // -------------------------------------------------------------------------
  const [selectedAssessmentForDetail, setSelectedAssessmentForDetail] = useState(null);
  const [detailMode, setDetailMode] = useState("view");
  const [openAssessmentDetail, setOpenAssessmentDetail] = useState(false);

  const handleCloseAssessmentDetail = useCallback(async () => {
    if (detailMode === "edit") {
      assessmentsCacheRef.current.delete(requestKey);
      await fetchAssessments(true);
    }
    setOpenAssessmentDetail(false);
    setSelectedAssessmentForDetail(null);
  }, [detailMode, fetchAssessments, requestKey]);

  const goToView = (item) => {
    if (!resolveAssessmentId(item)) return;
    setSelectedAssessmentForDetail(item);
    setDetailMode("view");
    setOpenAssessmentDetail(true);

  };

  //   const goToView = (item) => {
  //   const id = resolveAssessmentId(item);
  //   if (!id) return;
  //   history.push(routes.asses_lib_view.replace(":id", String(id)), {
  //     assessment: item,
  //     metaData,
  //     metaDataOptions,
  //   });
  // };

  const goToEdit = (item) => {
    if (!resolveAssessmentId(item)) return;
    setSelectedAssessmentForDetail(item);
    setDetailMode("edit");
    setOpenAssessmentDetail(true);

  };

  //   const goToEdit = (item) => {
  //   const id = resolveAssessmentId(item);
  //   if (!id) return;
  //   history.push(routes.asses_lib_edit.replace(":id", String(id)), {
  //     assessment: item,
  //     metaData,
  //     metaDataOptions,
  //   });
  // };

  //------------------------------------------------------------------------------
  // open add assessment
  //-------------------------------------------------------------------------

  const goToAdd = () => {
    setOpenAdd(true);
    // history.push(routes.asses_lib_add, { metaData, metaDataOptions });
  };
  //---------------------------------------------------------------------------------
  // Navigation helper for setup page------------------@@@@@@@@@@@@@@@@@@----------------
  // -------------------------------------------------------------------------
  const [SelectedAssessmentForSetup, setSelectedAssessmentForSetup] = useState(null);
  const [openSetup, setOpenSetup] = useState(false);
  const goToSetup = (item) => {
    setSelectedAssessmentForSetup(item);
    setOpenSetup(true);


    // const id = resolveAssessmentId(item);
    // if (!id) return;
    // history.push(`${routes.asses_lib}/setup/${id}`, {
    //   assessment: item,
    //   metaData,
    //   metaDataOptions: metaDataOptions,
    // });

  };

  const handleCloseSetup = useCallback(
    async (nextValue) => {
      if (nextValue !== false) {
        setOpenSetup(nextValue);
        return;
      }

      assessmentsCacheRef.current.delete(requestKey);
      await fetchAssessments(true);
      setOpenSetup(false);
    },
    [fetchAssessments, requestKey]
  );

  // Close add assessment form and refresh list
  const handleCloseAdd = useCallback(
    async () => {
      assessmentsCacheRef.current.delete(requestKey);
      await fetchAssessments(true);
      setOpenAdd(false);
    },
    [fetchAssessments, requestKey]
  );

  const onDelete = async (item) => {
    const id = resolveAssessmentId(item);
    if (!id) return;
    let confirm = false;
    try {
      confirm = await sweetConfirmAlert(
        "Delete Assessment",
        `Are you sure you want to delete "${item.name}"? This action cannot be undone.`,
        "warning",
      );
    } catch (error) {
      return;
    }
    if (!confirm) return;
    try {
      await assessSectionsService.deleteAssessment(id);
      assessmentsCacheRef.current.delete(requestKey);
      setFilters((prev) => ({ ...prev }));
      await sweetSuccessAlert(
        "Assessment Deleted",
        `"${item.name}" has been successfully deleted.`,
      );
    } catch (error) {
      await sweetErrorAlert(
        "Failed to delete assessment",
        getErrorMessage(error, "This paper cannot be deleted.")
      );
    }
  };

  const updateAssessmentCardStatus = useCallback(
    async (item, nextStatus, confirmTitle, confirmText, successTitle, successText) => {
      const assessmentId = resolveAssessmentId(item);
      if (!assessmentId) return;

      let confirm = false;
      try {
        confirm = await sweetConfirmAlert(confirmTitle, confirmText, "warning");
      } catch (error) {
        return;
      }

      if (!confirm) return;

      try {
        const response = await assessSectionsService.updateAssessmentStatus(assessmentId, {
          status: nextStatus,
        });

        if (response?.success === false) {
          await sweetErrorAlert(
            successTitle,
            response?.message || "Unable to update assessment status."
          );
          return;
        }

        assessmentsCacheRef.current.delete(requestKey);
        await fetchAssessments(true);
        await sweetSuccessAlert(successTitle, successText, "success");
      } catch (error) {
        await sweetErrorAlert(
          successTitle,
          getErrorMessage(error, "Unable to update assessment status.")
        );
      }
    },
    [fetchAssessments, requestKey]
  );

  const onPublishAssessment = useCallback(
    (item) =>
      updateAssessmentCardStatus(
        item,
        "Published",
        "Publish Assessment",
        `Publish "${item.name}" now?`,
        "Assessment Published",
        `"${item.name}" has been published successfully.`
      ),
    [updateAssessmentCardStatus]
  );

  const onCancelAssessmentPublish = useCallback(
    (item) =>
      updateAssessmentCardStatus(
        item,
        "Ready",
        "Remove From Publish",
        `Move "${item.name}" back to Ready?`,
        "Publish Cancelled",
        `"${item.name}" has been moved back to Ready.`
      ),
    [updateAssessmentCardStatus]
  );


  // -------------------------------------------------------------------------
  // Render
  // -------------------------------------------------------------------------

  if (openAdd) {
    return (
      <AssessmentForm
        metaData={metaData}
        metaDataOptions={metaDataOptions}
        goBack={() => setOpenAdd(false)}
        goBackWithRefresh={handleCloseAdd}
      />
    );
  }

  if (openAssessmentDetail && selectedAssessmentForDetail) {
    return (
      <AssessmentDetailPage
        mode={detailMode}
        assessment={selectedAssessmentForDetail}
        metaData={metaData}
        metaDataOptions={metaDataOptions}
        onClose={handleCloseAssessmentDetail}
      />
    );
  }

  return (
    <div>


      {/* //--------------------------
// Conditional rendering for SetupAssessmentPage
//--------------------------  */}
      {openSetup && SelectedAssessmentForSetup ? (
        <SetupAssessmentPage
          assessment={SelectedAssessmentForSetup}
          metaDataOptions={metaDataOptions}
          setOpenSetup={handleCloseSetup}
        />
      ) : (

        // --------------------------
        // Main Assessment Library UI
        // --------------------------

        <ContentWrapper>
          <section className="assess-section">
            <SectionTopicHeader
              className="assess-topic-header"
              title="Assessment Library"
              subtitle="Manage created and shared assessment drafts"
              icon={<HiClipboardDocumentList />}
              color="#734EBC"
              colorAlt="#D97706"
            />

            <div className="assess-toprow">
              <div className="assess-tabs" role="tablist" aria-label="Assessment library tabs">
                <button
                  type="button"
                  className={`assess-tab ${activeTab === "my" ? "active" : ""}`}
                  onClick={() => setActiveTab("my")}
                >
                  My Assessments
                </button>
                <button
                  type="button"
                  className={`assess-tab ${activeTab === "shared" ? "active" : ""}`}
                  onClick={() => setActiveTab("shared")}
                >
                  All Assessments
                </button>
              </div>

              <div className="assess-toprow__actions">
                <AddButton
                  label="Add Assessment"
                  // onClick={() => history.push(routes.asses_lib_add, { metaData, metaDataOptions })}
                  onClick={goToAdd}
                />
              </div>
            </div>

            <Filter
              metaDataOptions={metaDataOptions}
              onQueryChange={handleFilterQueryChange}
              apiFilters={filters}
              syncKey={`assessment-library:${activeTab}`}
              count={totalCount}
            />


            {isMetaDataLoading ? (
              <ContentWrapper>
                <div style={{ padding: 24 }}>
                  <Stack spacing={2}>
                    {/* <Skeleton variant="text" sx={{ fontSize: "1.5rem", width: 300 }} /> */}
                    <Skeleton variant="rectangular" width="100%" height={48} />

                    {/* list placeholders */}
                    {Array.from({ length: 6 }).map((_, i) => (
                      <Stack key={i} direction="row" spacing={2} alignItems="center">
                        {/* <Skeleton variant="circular" width={40} height={40} /> */}
                        <Skeleton variant="rectangular" width="100%" height={50} />
                        {/* <Skeleton variant="text" sx={{ fontSize: "0.9rem", width: "20%" }} /> */}
                      </Stack>
                    ))}
                  </Stack>
                </div>
              </ContentWrapper>
            ) : (
              <AssessSectionList
                activeTab={activeTab}
                items={filteredItems}
                onView={goToView}
                onEdit={goToEdit}
                onDelete={onDelete}
                onSetup={goToSetup}
                onPublish={onPublishAssessment}
                onCancelPublish={onCancelAssessmentPublish}
                page={currentPage}
                pageSize={filters.limit}
                totalCount={totalCount}
                onPageChange={handlePageChange}
                onPageSizeChange={handlePageSizeChange}
              />
            )}
          </section>
        </ContentWrapper>

      )}
    </div>
  );
}

